import React, { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import 'react-day-picker/dist/style.css';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { KeenIcon } from '@/components/keenicons';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

const DateInput = ({ value, label, onChange, mode = 'single', customClass, showLabel = true, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const isRange = mode === 'range';

    const stripTime = (d) => {
        if (!d) return null;
        const date = new Date(d);
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    };

    const formatDate = (d) => (d ? format(d, 'yyyy-MM-dd') : null);

    const handleClearDate = () => {
        setOpen(false);
        onChange(isRange ? { from: null, to: null } : null);
    };

    const handleSelectToday = () => {
        const today = stripTime(new Date());
        if (isRange) {
            onChange({
                from: formatDate(today),
                to: formatDate(today),
            });
        } else {
            onChange(formatDate(today));
        }
        setOpen(false);
    };

    const handleSelect = (selected) => {
        if (isRange) {
            const from = stripTime(selected?.from);
            const to = stripTime(selected?.to);

            const result = {
                from,
                to,
                formatted: {
                    from: formatDate(from),
                    to: formatDate(to),
                },
            };

            onChange(result.formatted);

            if (from && to) setOpen(false);
        } else {
            const date = stripTime(selected);
            onChange(formatDate(date));
            setOpen(false);
        }
    };

    const normalizeTwoDigitYear = (year) => {
        if (year >= 100) return year;
        return 2000 + year;
    };

    const parseFlexibleDate = (str) => {
        str = str.trim();

        // YYYY-M-D or YYYY/MM/DD
        const yyyy = /^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/;
        const m1 = str.match(yyyy);
        if (m1) {
            const [, y, mo, d] = m1;
            return new Date(Number(y), Number(mo) - 1, Number(d));
        }

        // MM/DD/YY
        if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(str)) {
            const [m, d, y] = str.split('/');
            const year = normalizeTwoDigitYear(parseInt(y, 10));
            return new Date(year, Number(m) - 1, Number(d));
        }

        // MM/DD/YYYY
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
            const [m, d, y] = str.split('/');
            return new Date(Number(y), Number(m) - 1, Number(d));
        }

        return null;
    };



    const handleSearchEnter = (e) => {
        if (e.key !== "Enter") return;

        const q = search.trim();
        if (!q) return;

        const isRangeInput = isRange && /\s-\s/.test(q);
        if (isRangeInput) {
            const [left, right] = q.split(/\s-\s/).map((s) => s.trim());
            const d1 = parseFlexibleDate(left);
            const d2 = parseFlexibleDate(right);

            if (d1 && d2) {
                handleSelect({ from: d1, to: d2 });
            }
            return;
        }

        if (!isRange) {
            const d = parseFlexibleDate(q);
            if (d) handleSelect(d);
        }
    };


    return (
        <div className={cn(customClass, 'w-full')}>
            {showLabel && <label className='mb-1 w-fit form-label'>{label}</label>}

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button disabled={disabled} className={cn('btn btn-sm btn-light', !value?.from && 'text-gray-400', 'h-[38px] w-full')}>
                        <KeenIcon icon="calendar" className="me-0.5" />
                        {isRange ? (
                            value?.from && value?.to
                                ? `${format(value.from, 'LLL dd, y')} - ${format(value.to, 'LLL dd, y')}`
                                : label
                        ) : (
                            value ? `${format(value, 'LLL dd, y')}` : label
                        )}
                    </button>
                </PopoverTrigger>

                <PopoverContent className="w-auto p-0" align="end">

                    {/* 🔍 Search bar */}
                    <div className="p-3 pb-0">
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleSearchEnter}
                            placeholder={
                                isRange
                                    ? "Enter range: (MM/DD/YY - MM/DD/YY)"
                                    : "Search date: (MM/DD/YY)"
                            }
                            className="input"
                        />
                    </div>

                    <Calendar
                        mode={mode}
                        selected={value}
                        onSelect={handleSelect}
                        numberOfMonths={isRange ? 2 : 1}
                        showOutsideDays
                        captionLayout="dropdown"
                        fromYear={1950}
                        toYear={new Date().getFullYear()}
                        classNames={{
                            caption_dropdowns: "flex gap-2 mt-1 justify-center",
                            dropdown:
                                "h-8 max-w-[130px] truncate rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-0 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none",
                        }}
                    />

                    <div className="flex items-center gap-2 mx-3 mb-3">
                        <button
                            type="button"
                            onClick={handleSelectToday}
                            className="btn btn-xs btn-outline btn-primary"
                        >
                            Today
                        </button>
                        <button
                            type="button"
                            onClick={handleClearDate}
                            className="btn btn-xs btn-outline btn-danger"
                        >
                            Clear Date
                        </button>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export { DateInput };
